import { action, makeObservable, observable } from 'mobx';
import { RootStore } from './root';
import { VerifyCodeStore } from './verifyCode';
import { VerificationState } from '../components/PostLogin/VerifyButton';
import {
  EMAIL_CONFIRM_ENFORCEMENT,
  SESSION_STORAGE_KEYS,
} from '../utils/constants';
import { PostSignupPayload } from '../types';
import { ReportProps } from '@wix/web-bi-logger/dist/src/types';
import {
  postSignupEmailConfirmationErrorSrc5Evid1940,
  postSignupEmailConfirmationEmailSentFailedSrc5Evid1942,
  postSignupEmailConfirmationCodeSendSuccessSrc5Evid1944,
  postSignupEmailConfirmationEmailVerifiedSrc5Evid1946,
} from '@wix/bi-logger-identity-data/v2';
import { BI_ORIGIN, BI_ERROR_TYPE } from './constants';
import { isNumber } from 'lodash';
import { ERROR_CODES } from '../utils/errorHandler';

export class PostSignupEmailConfirmStore extends VerifyCodeStore {
  public isLoading: boolean = false;
  public email: string = '';
  public verificationId: string = '';
  public registrationPayload: PostSignupPayload = {};

  constructor(rootStore: RootStore) {
    super(rootStore, 10);
    this.verificationState = VerificationState.Unverified;
    this.loadFromSessionStorage();

    makeObservable(this, {
      isLoading: observable,
      email: observable,
      verificationId: observable,
      registrationPayload: observable,
      sendVerificationEmail: action.bound,
      setRegistrationPayload: action.bound,
    });
  }

  private loadFromSessionStorage(): void {
    this.email =
      sessionStorage.getItem(SESSION_STORAGE_KEYS.PENDING_EMAIL_CONFIRMATION) ??
      '';
    this.verificationId =
      sessionStorage.getItem(SESSION_STORAGE_KEYS.VERIFICATION_ID) ?? '';
    const payload = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.REGISTRATION_PAYLOAD,
    );
    this.registrationPayload = payload ? JSON.parse(payload) : {};
  }

  public clearEmailConfirmationStorage(): void {
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.PENDING_EMAIL_CONFIRMATION);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.REGISTRATION_PAYLOAD);
    sessionStorage.removeItem(SESSION_STORAGE_KEYS.VERIFICATION_ID);
  }

  public enableAccess() {
    return !!this.email;
  }

  public setRegistrationPayload(payload: any) {
    this.registrationPayload = {
      uaToken: payload?.uaToken,
      ssoUrl: payload?.ssoUrl,
      authMethod: payload?.authMethod,
    };
    sessionStorage.setItem(
      SESSION_STORAGE_KEYS.REGISTRATION_PAYLOAD,
      JSON.stringify(this.registrationPayload),
    );
  }

  public async sendVerificationEmail(email: string) {
    try {
      this.email = email;
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.PENDING_EMAIL_CONFIRMATION,
        email,
      );
      const response =
        await this.rootStore.accountSettingsApi.sendVerificationCode({
          email,
          incognito: true,
          isActionTagRequired: true,
          flow: EMAIL_CONFIRM_ENFORCEMENT,
        });
      this.verificationId = response?.data?.verificationId || '';
      if (this.verificationId) {
        sessionStorage.setItem(
          SESSION_STORAGE_KEYS.VERIFICATION_ID,
          this.verificationId,
        );
      }
      this.sendBiEvent(
        postSignupEmailConfirmationCodeSendSuccessSrc5Evid1944({
          origin: BI_ORIGIN.POST_SIGNUP,
        }),
      );
      return response?.data?.result?.success || response?.status === 200;
    } catch (error) {
      this.sendBiEvent(
        postSignupEmailConfirmationEmailSentFailedSrc5Evid1942({
          origin: BI_ORIGIN.POST_SIGNUP,
        }),
      );
      throw error;
    }
  }

  public async onResendConfirmationCode() {
    const response =
      await this.rootStore.accountSettingsApi.sendVerificationCode({
        email: this.email,
        incognito: true,
        isActionTagRequired: true,
        flow: EMAIL_CONFIRM_ENFORCEMENT,
      });
    this.verificationId = response?.data?.verificationId || '';
    if (this.verificationId) {
      sessionStorage.setItem(
        SESSION_STORAGE_KEYS.VERIFICATION_ID,
        this.verificationId,
      );
    }
    this.sendBiEvent(
      postSignupEmailConfirmationCodeSendSuccessSrc5Evid1944({
        origin: BI_ORIGIN.POST_SIGNUP,
      }),
    );
    return response;
  }

  public async onVerifyConfirmationCode() {
    try {
      const success = await this.rootStore.accountSettingsApi.verifyEmailCode({
        verificationCode: this.confirmCode.value,
        verificationId: this.verificationId,
      });

      if (success) {
        this.sendBiEvent(
          postSignupEmailConfirmationEmailVerifiedSrc5Evid1946({
            Origin: BI_ORIGIN.POST_SIGNUP,
          }),
        );
        this.clearEmailConfirmationStorage();
        // After successful verification, complete the signup flow
        return this.rootStore.navigationStore.postSignup(
          this.registrationPayload,
        );
      }

      throw this.rootStore.accountSettingsApi.handleError({
        data: { errorCode: 8005 },
      });
    } catch (error: any) {
      throw this.handleError(error);
    }
  }

  public async verifyCode() {
    try {
      if (!this.confirmCode.isValid) {
        this.sendBiEvent(
          postSignupEmailConfirmationErrorSrc5Evid1940({
            origin: BI_ORIGIN.POST_SIGNUP,
            type: BI_ERROR_TYPE.INCORRECT_CODE,
          }),
        );
        this.confirmCode.markFieldAsDirty();
        return;
      }
      this.verificationState = VerificationState.Verifying;
      await this.onVerifyConfirmationCode();
      // Don't set to Verified - keep loading until navigation completes
    } catch (error: any) {
      this.verificationState = VerificationState.Unverified;
      this.sendBiEvent(
        postSignupEmailConfirmationErrorSrc5Evid1940({
          origin: BI_ORIGIN.POST_SIGNUP,
          type:
            error?.errorReason === 'emailConfirmation.signUp.error.wrongCode'
              ? BI_ERROR_TYPE.INCORRECT_CODE
              : BI_ERROR_TYPE.EXPIRED_CODE,
        }),
      );
      this.confirmCode.addError(this.rootStore.i18n.t(error?.errorReason));
    }
  }

  sendBiEvent(props: ReportProps) {
    this.rootStore.biLogger.report(props).catch();
  }

  handleError(response: any) {
    const errorCode = response?.response?.data?.errorCode;
    const errorCodeStr =
      errorCode && isNumber(errorCode) ? errorCode.toString() : errorCode;
    const errorReason =
      errorCodeStr === ERROR_CODES.BAD_CODE
        ? 'emailConfirmation.signUp.error.wrongCode'
        : 'emailConfirmation.signUp.error.expireCode';

    return { success: false, errorCode, errorReason };
  }
}
